import * as React from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";

import { cn } from "../../lib/utils";

/**
 * $10M Tooltip (glassmorphism + premium shadow + cleaner motion)
 * - Dark-mode by default
 * - Subtle gradient sheen + glow
 * - Crisp border + depth shadows
 * - Smooth "float-in" animation
 */

function TooltipProvider({ delayDuration = 120, ...props }) {
  return (
    <TooltipPrimitive.Provider
      data-slot="tooltip-provider"
      delayDuration={delayDuration}
      {...props}
    />
  );
}

function Tooltip({ ...props }) {
  // Keeping your existing API (Tooltip wraps Provider) for drop-in compatibility.
  // If you want best perf: use <TooltipProvider> once at app root and export Root directly.
  return (
    <TooltipProvider>
      <TooltipPrimitive.Root data-slot="tooltip" {...props} />
    </TooltipProvider>
  );
}

function TooltipTrigger({ ...props }) {
  return <TooltipPrimitive.Trigger data-slot="tooltip-trigger" {...props} />;
}

function TooltipContent({
  className,
  sideOffset = 10,
  arrowColor = "fill-[#0B0B12]/85",
  children,
  ...props
}) {
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        style={{ zIndex: 9999 }}
        data-slot="tooltip-content"
        sideOffset={sideOffset}
        className={cn(
          // Layout
          "relative z-50 max-w-[320px] overflow-hidden rounded-2xl px-3.5 py-2.5",
          // Glass base
          "bg-[#0B0B12]/75 backdrop-blur-xl",
          // Border + inner highlight
          "border border-white/10 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]",
          // Premium depth + glow
          "shadow-[0_18px_70px_-32px_rgba(0,0,0,0.9),0_16px_48px_-30px_rgba(99,102,241,0.45),0_10px_30px_-24px_rgba(236,72,153,0.20)]",
          // Typography
          "text-xs font-medium text-white/90 tracking-[0.01em]",
          // Smooth motion (Radix sets data-state='delayed-open')
          "will-change-transform",
          "data-[state=delayed-open]:animate-in data-[state=delayed-open]:fade-in-0 data-[state=delayed-open]:zoom-in-95",
          "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
          // Directional slide (small, premium)
          "data-[side=bottom]:slide-in-from-top-1",
          "data-[side=top]:slide-in-from-bottom-1",
          "data-[side=left]:slide-in-from-right-1",
          "data-[side=right]:slide-in-from-left-1",
          // Gradient sheen + subtle glow overlay
          "before:content-[''] before:pointer-events-none before:absolute before:inset-0 before:rounded-2xl",
          "before:bg-[radial-gradient(120px_60px_at_18%_0%,rgba(99,102,241,0.35),transparent_70%),radial-gradient(140px_80px_at_85%_120%,rgba(236,72,153,0.22),transparent_70%)]",
          "before:opacity-80",
          // Thin top sheen
          "after:content-[''] after:pointer-events-none after:absolute after:inset-0 after:rounded-2xl",
          "after:bg-[linear-gradient(to_bottom,rgba(255,255,255,0.10),transparent_40%)]",
          "after:opacity-70",
          className
        )}
        {...props}
      >
        <div className="relative z-10">{children}</div>

        <TooltipPrimitive.Arrow
          className={cn(
            arrowColor,
            "drop-shadow-[0_10px_25px_rgba(0,0,0,0.55)]",
            "translate-y-[0.5px]"
          )}
        />
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  );
}

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider };
